import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import axios from 'axios';

interface Post {
  id: number;
  title: {
    rendered: string;
  };
  acf: {
    latitude: string;
    longitude: string;
    city: string;
    state: string;
    pastor_photo: string;
    church_name: string;
    include_on_map: boolean | string | number;
  };
}

interface MapProps {
  apiKey: string;
}

const GoogleMapComponent: React.FC<MapProps> = ({ apiKey }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<Post | null>(null);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey
  });
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const mapRef = useRef<any>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [hideList, setHideList] = useState(false);

  const handleListItemClick = (post: Post, index: number) => {
    setSelectedMarker(post);
    setClickedIndex(index);
    if (map) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(new google.maps.LatLng(parseFloat(post.acf.latitude), parseFloat(post.acf.longitude)));
      map.fitBounds(bounds);

      // Set zoom level to ensure the selected marker is well-displayed
      let zoom = map.getZoom() || 12; // Default zoom level or fallback to 10 if undefined
      zoom = zoom < 10 ? zoom : 12; // Ensure the minimum zoom level is 10
      console.log(zoom);
      map.setZoom(zoom);
    }
  };

  useEffect(() => {
      // Access the data attribute from the root element
      const rootElement = document.getElementById('root');
      if (rootElement) {
          const hideListData = rootElement.getAttribute('data-hide-list');
          setHideList(hideListData === 'true');
      }
  }, []);

  useEffect(() => {
    // Fetch data from WordPress API
    axios
      .get<Post[]>('https://www.lakewoodlife.org/wp-json/wp/v2/churches', {
        params: {
          acf_format: 'standard',
          per_page: 40, // Request up to 40 posts
          orderby: 'title', // Order posts alphabetically by title
          order: 'asc'
        }
      })
      .then(response => {
        // Only keep posts where include_on_map is true
        const filteredPosts = response.data.filter(post => {
          const value = post.acf?.include_on_map;
          return value !== false && value !== '0' && value !== 0;
        });

        setPosts(filteredPosts);

        // Calculate the average latitude and longitude
        const totalLat = filteredPosts.reduce((sum, post) => sum + parseFloat(post.acf.latitude), 0);
        const totalLng = filteredPosts.reduce((sum, post) => sum + parseFloat(post.acf.longitude), 0);
        const avgLat = totalLat / filteredPosts.length;
        const avgLng = totalLng / filteredPosts.length;

        setMapCenter({ lat: avgLat, lng: avgLng });
        setIsMapLoaded(true);
      })
      .catch(error => {
        console.error('Error fetching data from WordPress API:', error);
      });
  }, []);

  if (loadError) return <div>Error loading Google Maps API</div>;
  if (!isLoaded || !isMapLoaded) return <div className="text-center text-[24px] font-bold text-[#3b3c3e]">Loading...</div>;

  const onLoad = (map) => {
    const bounds = new google.maps.LatLngBounds();
    posts.forEach(post => {
      bounds.extend(new window.google.maps.LatLng(parseFloat(post.acf.latitude), parseFloat(post.acf.longitude)));
    });
    map.fitBounds(bounds);
    setTimeout(() => {
      map.setCenter(mapCenter);
    }, 1000); // Adjust the delay as needed

    setMap(map)
  };

  return (
    <div className="flex flex-col-reverse md:flex-row">
      {!hideList && (
        <div className="w-full md:w-5/12 lg:w-4/12 p-6">
          {posts.map((post, index) => (
            <div key={index} onClick={() => handleListItemClick(post, index)} className={`w-full border-solid border-b border-[#bbc5cc] last:border-b-0 mb-6 pb-6 cursor-pointer`}>
              <div className="flex gap-4 items-center">
                <figure className="w-[100px] h-[125px] overflow-hidden"><img className="object-cover w-full h-full object-center" src={post.acf.pastor_photo} alt={post.title.rendered} /></figure>
                <div className="text-[#3b3c3e]">
                  <span className={`block rounded-full text-[12px] leading-[24px] text-center text-white font-bold w-[24px] h-[24px] mb-2 ${clickedIndex === index ? 'bg-[#003e6c]' : 'bg-[#3b3c3e]'}`}>{index + 1}</span>
                  <h2 className="text-[24px] font-bold text-[#3b3c3e] m-0">{post.title.rendered}</h2>
                  {post.acf.church_name && (<span className="block font-bold">{post.acf.church_name}</span>)}
                  {post.acf.city && (<>{post.acf.city},</>)} {post.acf.state}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={`${!hideList ? 'md:sticky md:top-0 w-full md:w-7/12 lg:w-8/12 h-[300px] md:h-screen' : 'w-full aspect-video'}`}>
        {posts.length && (
          <GoogleMap
            id="map"
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={5}
            onLoad={onLoad}
            ref={mapRef}
          >
            {posts.map((post, index) => {
              const position = { lat: parseFloat(post.acf.latitude), lng: parseFloat(post.acf.longitude) };
              const markerLabel = !hideList ? {
                text: (index + 1).toString(),
                color: 'white',
                fontWeight: 'bold'
            } : undefined;
              return (
                <Marker
                  key={post.id}
                  position={position}
                  title={post.title.rendered}
                  label={markerLabel}
                  onClick={() => setSelectedMarker(post)}
                />
              );
            })}
            {selectedMarker && (
              <InfoWindow
                position={{ lat: parseFloat(selectedMarker.acf.latitude), lng: parseFloat(selectedMarker.acf.longitude) }}
                onCloseClick={() => setSelectedMarker(null)}
                options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
              >
                <div>
                  <h2 className="text-[16px] m-0">{selectedMarker.title.rendered}</h2>
                  {selectedMarker.acf.church_name && (<span className="block mt-1">{selectedMarker.acf.church_name}</span>)}
                  {/* Additional information or content here */}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>
    </div>
  );
};

export default GoogleMapComponent;
